"use client";

import { useRef, useState, useTransition } from "react";
import Link from "next/link";
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  MailCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AltchaField } from "@/components/auth/altcha-field";
import { signupAction } from "@/lib/api/signup-actions";
import { signupGoogleAction } from "@/lib/api/google-actions";
import { cn } from "@/lib/utils";

// Unicode "Combining Diacritical Marks" block (hex 0x0300-0x036f), built via
// fromCharCode rather than a \u escape literal to dodge a source-encoding
// pitfall with combining code points sitting directly in this file.
const COMBINING_MARKS_RE = new RegExp(
  `[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`,
  "g",
);

// Company name -> slug suggestion. Polish diacritics fold to ASCII (ł has no
// NFD decomposition — handled explicitly); everything else non-alphanumeric
// collapses to single dashes; max 31 chars (backend slug rule).
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(COMBINING_MARKS_RE, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 31)
    .replace(/-+$/g, "");
}

export function SignupForm({
  tosVersion,
  dpaVersion,
  google,
}: {
  tosVersion: string;
  dpaVersion: string;
  google?: { ott: string; email: string; name: string };
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [consents, setConsents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, startTransition] = useTransition();
  // Remount counter — a consumed ALTCHA payload authorises exactly one
  // attempt, so any failed submit re-arms the widget (login-form idiom).
  const [captchaKey, setCaptchaKey] = useState(0);
  const altchaPayload = useRef<string | null>(null);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const value = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement).value;
    setError(null);
    if (!google) {
      const password = value("password");
      const password2 = value("password2");
      if (password !== password2) {
        setError("Hasła różnią się od siebie.");
        return;
      }
    }
    if (!consents) {
      setError("Zaakceptuj Regulamin i Umowę powierzenia danych (DPA).");
      return;
    }
    startTransition(async () => {
      if (google) {
        const res = await signupGoogleAction({
          ott: google.ott,
          companyName: value("companyName"),
          nip: value("nip"),
          slug: slug.trim().toLowerCase(),
          adminName: value("adminName"),
          consentsAccepted: consents,
        });
        // Success never returns (server-side redirect to /panel).
        if (res.status === "slug_taken") {
          setError("Ten adres panelu jest już zajęty — wybierz inny.");
        } else if (res.status === "expired") {
          setError(
            "Sesja Google wygasła. Kliknij „Zarejestruj się przez Google” ponownie.",
          );
        } else if (res.status === "disabled") {
          setError("Rejestracja jest obecnie niedostępna.");
        } else if (res.status === "invalid") {
          setError(res.message);
        } else {
          setError("Rejestracja nie powiodła się. Spróbuj ponownie.");
        }
        return;
      }
      const password = value("password");
      const res = await signupAction({
        companyName: value("companyName"),
        nip: value("nip"),
        slug: slug.trim().toLowerCase(),
        adminName: value("adminName"),
        email: value("email"),
        password,
        consentsAccepted: consents,
        altcha: altchaPayload.current ?? undefined,
      });
      if (res.status === "ok") {
        setDone(true);
        return;
      }
      const message =
        res.status === "slug_taken"
          ? "Ten adres panelu jest już zajęty — wybierz inny."
          : res.status === "disabled"
            ? "Rejestracja jest obecnie niedostępna."
            : res.status === "invalid"
              ? res.message
              : "Nie udało się wysłać zgłoszenia. Spróbuj ponownie.";
      setError(message);
      // Every server round-trip consumed the payload — re-arm the widget.
      altchaPayload.current = null;
      setCaptchaKey((k) => k + 1);
    });
  }

  if (done) {
    return (
      <div className="space-y-4" role="status">
        <MailCheck className="size-8 text-success" />
        <h2 className="text-xl font-bold tracking-tight text-foreground">
          Sprawdź skrzynkę e-mail
        </h2>
        <p className="text-muted-foreground">
          Wysłaliśmy link potwierdzający (dotarcie może potrwać do kilku
          minut). Link jest ważny 24 godziny. Jeśli nie dotrze — wyślij
          formularz ponownie, poprzedni link zostanie zastąpiony nowym.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="companyName">Nazwa firmy</Label>
        <Input
          id="companyName"
          name="companyName"
          required
          minLength={2}
          maxLength={200}
          placeholder="Instalacje Nowak Sp. z o.o."
          className="h-11"
          onChange={(e) => {
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="nip">NIP</Label>
          <Input
            id="nip"
            name="nip"
            required
            maxLength={20}
            placeholder="526-025-02-74"
            className="h-11"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="slug">Adres panelu (identyfikator)</Label>
          <Input
            id="slug"
            name="slug"
            required
            value={slug}
            autoComplete="off"
            pattern="[a-z0-9][a-z0-9-]{1,30}"
            title="Małe litery, cyfry i myślniki (2–31 znaków)"
            placeholder="instalacje-nowak"
            className="h-11"
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value.toLowerCase());
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="adminName">Imię i nazwisko administratora</Label>
        <Input
          id="adminName"
          name="adminName"
          required
          minLength={2}
          maxLength={200}
          autoComplete="name"
          placeholder="Jan Nowak"
          defaultValue={google?.name}
          className="h-11"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">
          Adres e-mail{google ? " (z konta Google)" : ""}
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="jan.nowak@firma.pl"
          className={cn("h-11", google && "opacity-70")}
          {...(google
            ? { defaultValue: google.email, readOnly: true, "aria-readonly": true }
            : {})}
        />
      </div>

      {!google ? (
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="password">Hasło (min. 8 znaków)</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="••••••••"
                className="h-11 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
                className="absolute inset-y-0 right-0 flex w-10 cursor-pointer items-center justify-center text-muted-foreground transition-colors duration-200 hover:text-foreground"
              >
                {showPassword ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password2">Powtórz hasło</Label>
            <Input
              id="password2"
              name="password2"
              type={showPassword ? "text" : "password"}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="••••••••"
              className="h-11"
            />
          </div>
        </div>
      ) : null}

      <div className="flex items-start gap-2">
        <Checkbox
          id="consents"
          checked={consents}
          onCheckedChange={(value) => setConsents(Boolean(value))}
          className="mt-0.5"
        />
        <Label htmlFor="consents" className="cursor-pointer font-normal">
          Akceptuję{" "}
          <Link
            href="/regulamin"
            target="_blank"
            className="underline underline-offset-4"
          >
            Regulamin
          </Link>{" "}
          (wersja {tosVersion}) oraz{" "}
          <Link
            href="/dpa"
            target="_blank"
            className="underline underline-offset-4"
          >
            Umowę powierzenia danych (DPA)
          </Link>{" "}
          (wersja {dpaVersion})
        </Label>
      </div>

      {error ? (
        <div
          role="alert"
          className="flex items-start gap-2.5 rounded-xl border border-destructive/20 bg-destructive/8 px-3.5 py-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      {/* ALTCHA is mandatory on classic signup (spec §7) — always rendered,
          auto-solves. Skipped in Google mode: identity already verified via OTT. */}
      {!google ? (
        <AltchaField
          key={captchaKey}
          onVerified={(payload) => {
            altchaPayload.current = payload;
          }}
        />
      ) : null}

      <Button
        type="submit"
        size="lg"
        disabled={pending}
        className="h-11 w-full gap-2 rounded-xl text-sm transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.99]"
      >
        {pending ? <Loader2 className="size-4 animate-spin" /> : null}
        {pending ? "Wysyłanie…" : "Załóż konto"}
      </Button>
      {!google ? (
        // Google mode provisions and signs in immediately — this classic-flow
        // promise about e-mail confirmation would be false there.
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5" />
          Konto powstanie dopiero po potwierdzeniu adresu e-mail.
        </p>
      ) : null}
    </form>
  );
}
